import { ComponentType } from '@angular/cdk/portal';
import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

/**
 * Size presets for {@link ResponsiveDialogService}. Each preset maps to a
 * (width / maxWidth / height / maxHeight) tuple tuned for the existing
 * `responsive-modal` SCSS rules in `src/styles/styles.scss`. Pass `width` /
 * `maxWidth` / `height` / `maxHeight` directly to override.
 */
export type ResponsiveDialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ResponsiveDialogOptions<TData = unknown>
  extends Omit<MatDialogConfig<TData>, 'data'> {
  data?: TData;
  size?: ResponsiveDialogSize;
}

const SIZE_PRESETS: Record<
  ResponsiveDialogSize,
  Pick<MatDialogConfig, 'width' | 'maxWidth' | 'height' | 'maxHeight'>
> = {
  sm: { width: '400px', maxWidth: '95vw' },
  md: { width: '720px', maxWidth: '95vw', maxHeight: '90vh' },
  lg: { width: '95vw', maxWidth: '1100px', height: '90vh' },
  xl: { width: '95vw', maxWidth: '1400px', height: '90vh' },
  full: { width: '98vw', maxWidth: '98vw', height: '95vh', maxHeight: '95vh' },
};

const DEFAULT_PANEL_CLASS = 'responsive-modal';

/**
 * Generic wrapper around `MatDialog` that bakes in our standard responsive
 * modal panelClass and a small set of size presets. Use it instead of calling
 * `MatDialog.open(...)` directly when opening a dialog that should pick up
 * the `responsive-modal` styling.
 *
 * Specialized confirmation modals still live in {@link ConfirmationService}.
 *
 * @example
 * ```ts
 * const ref = this.responsiveDialog.open<MyModalComponent, MyData, boolean>(
 *   MyModalComponent,
 *   { size: 'xl', data: { id: '123' } },
 * );
 * ref.afterClosed().subscribe((confirmed) => { ... });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ResponsiveDialogService {
  private readonly dialog = inject(MatDialog);

  open<TComponent, TData = unknown, TResult = unknown>(
    component: ComponentType<TComponent>,
    options: ResponsiveDialogOptions<TData> = {},
  ): MatDialogRef<TComponent, TResult> {
    const { size = 'lg', panelClass, ...rest } = options;
    const preset = SIZE_PRESETS[size];

    const mergedPanelClasses = this.mergePanelClasses(panelClass);

    const config: MatDialogConfig<TData> = {
      autoFocus: false,
      restoreFocus: true,
      ...preset,
      ...rest,
      panelClass: mergedPanelClasses,
    };

    return this.dialog.open<TComponent, TData, TResult>(component, config);
  }

  private mergePanelClasses(
    userClasses: string | string[] | undefined,
  ): string[] {
    if (!userClasses) return [DEFAULT_PANEL_CLASS];
    const arr = Array.isArray(userClasses) ? userClasses : [userClasses];
    return arr.includes(DEFAULT_PANEL_CLASS) ? arr : [DEFAULT_PANEL_CLASS, ...arr];
  }
}
